import React, { useEffect } from 'react';
import PaymentGateway from '../PaymentGateway';
import CosmicCheckoutModal from '../cosmic-assessment/CosmicCheckoutModal';
import { trackEvent } from '../../utils/analytics';
import { useAssessment } from '../../contexts/AssessmentContext';
import { isDevToolsEnabled } from '../../utils/devMode';

/**
 * Checkout entry point used by usePayment().
 *
 * Real takers go through CosmicCheckoutModal, which creates a PaymentIntent via
 * the Firebase `createPaymentIntent` callable, confirms the card with Stripe,
 * and refreshes the tier custom claim the webhook grants. The legacy
 * PaymentGateway (old Vercel/PHP `/api/create-payment-intent.php`) is kept ONLY
 * behind devTestMode so the test-harness autopilot can mock-pay without a card.
 */
const CheckoutModal = ({ isOpen, onClose, targetTier, currentTier, userId, sourceContext, onSuccess }) => {
  const { devTestMode } = useAssessment();

  // Esc closes the lightweight dev/mock modal. CosmicCheckoutModal handles its own.
  useEffect(() => {
    if (!isOpen || !devTestMode) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose, devTestMode]);

  if (!isOpen) return null;

  // CosmicCheckoutModal + the server pricing map key on UPPERCASE tier names.
  const normTarget = String(targetTier || '').toUpperCase();
  const normCurrent = String(currentTier || 'FREE').toUpperCase();

  // Dev/test autopilot: instant mock pay so automated runs don't need a real card.
  // Double-gated so a real user can never reach the mock (no-Stripe) checkout.
  if (devTestMode && isDevToolsEnabled()) {
    return (
      <div className="checkout-backdrop" role="dialog" aria-modal="true" aria-label="Secure checkout">
        <div className="checkout-container" role="document">
          <button className="close-btn" onClick={onClose} aria-label="Close">×</button>
          <PaymentGateway
            onPaymentSuccess={(pi) => {
              trackEvent('payment_succeeded', { targetTier, currentTier, sourceContext, amount: pi?.amount_received, mock: true });
              if (onSuccess) onSuccess(normTarget, pi);
              onClose();
            }}
            onCancel={onClose}
            currentTier={currentTier}
            targetTier={targetTier}
            userId={userId}
            devMockOverride={true}
          />
        </div>
        <style jsx>{`
          .checkout-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display:flex; align-items:center; justify-content:center; z-index: 1000; }
        `}</style>
      </div>
    );
  }

  // Real Stripe checkout via the Firebase callable; the webhook grants the tier claim.
  return (
    <CosmicCheckoutModal
      isOpen
      onClose={onClose}
      targetTier={normTarget}
      currentTier={normCurrent}
      onSuccess={(pi) => {
        trackEvent('payment_succeeded', { targetTier: normTarget, currentTier: normCurrent, sourceContext, amount: pi?.amount });
        if (onSuccess) onSuccess(normTarget, pi);
        onClose();
      }}
      onError={(err) => {
        trackEvent('payment_failed', { targetTier: normTarget, currentTier: normCurrent, sourceContext, message: err?.message });
      }}
    />
  );
};

export default CheckoutModal;
